import { useCallback, ReactNode } from "react";

import Icon from "~/components/shared/Icon";
import useCopyToClipboard from "~/components/analytics/shared/hooks/useCopyToClipboard";
import classNames from "classnames";

interface CopyButtonProps {
  /** Text that should be written to the clipboard */
  contentToCopy: string;
  /** Render just the icon (no label). Automatically true for the overlay variant */
  iconOnly?: boolean;
  /** Additional className applied to the root element */
  className?: string;
  /** Button size (affects icon size) for inline variant */
  size?: "xs" | "sm";
  /** When true, renders the copy icon as an overlay that becomes visible on parent hover */
  overlay?: boolean;
  /** Content to render when using the overlay variant */
  children?: ReactNode;
  title?: string;
}

export default function CopyButton({
  title,
  contentToCopy,
  iconOnly,
  overlay = false,
  size = "sm",
  children,
  className,
}: CopyButtonProps): JSX.Element | null {
  const { copied, handleCopy } = useCopyToClipboard();

  const handleCopyClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.currentTarget.blur();
      handleCopy(contentToCopy);
    },
    [contentToCopy, handleCopy],
  );

  if (!navigator.clipboard || !window.isSecureContext) {
    // Fail silently if clipboard API is unavailable
    return overlay ? ((children ?? null) as JSX.Element | null) : null;
  }

  // ---------------------------------------------------------------------------
  // Overlay variant – copy icon appears on hover, positioned to the right edge
  // ---------------------------------------------------------------------------
  if (overlay) {
    return (
      <span
        data-copied={copied}
        className={classNames(
          "group relative inline-flex transition-all delay-100 duration-100 hover:pr-6 data-[copied=true]:pr-6",
          className,
        )}
      >
        {children}
        <span className="absolute right-0 top-1/2 -translate-y-1/2 scale-75 opacity-0 transition-all delay-0 duration-0 group-hover:scale-100 group-hover:opacity-100 group-hover:delay-150 group-hover:duration-75 group-data-[copied=true]:pointer-events-auto group-data-[copied=true]:scale-100 group-data-[copied=true]:opacity-100 group-data-[copied=true]:delay-150 group-data-[copied=true]:duration-75">
          <button
            title={title}
            type="button"
            onClick={handleCopyClick}
            className="pointer-events-none rounded-sm px-1.5 py-1.5 transition-colors hover:bg-slate-800/5 group-hover:pointer-events-auto"
          >
            {copied ? (
              <Icon icon="heroicons/16/solid/check" className="block size-3 text-green-700" />
            ) : (
              <Icon
                icon="heroicons/outline/square-2-stack"
                className="text-charcoal-400 block size-3"
              />
            )}
          </button>
        </span>
      </span>
    );
  }

  const iconSizes = {
    xs: 14,
    sm: 16,
  };

  return (
    <button
      type="button"
      onClick={handleCopyClick}
      aria-label={title ?? (iconOnly && copied ? "Copied" : "Copy")}
      data-title-tooltip={iconOnly && copied ? "Copied" : "Copy"}
      className={classNames(
        "inline-flex h-6 items-center justify-center gap-1 rounded-sm hover:bg-slate-800/5",
        iconOnly ? "w-6" : "px-1",
        className,
      )}
    >
      {copied ? (
        <Icon
          icon="heroicons/16/solid/check"
          style={{ width: iconSizes[size] }}
          className="block text-green-700"
        />
      ) : (
        <Icon
          icon="heroicons/outline/square-2-stack"
          style={{ width: iconSizes[size] }}
          className="hover:text-charcoal-500 block text-gray-900"
        />
      )}

      {!iconOnly && <span>{copied ? "Copied" : children}</span>}
    </button>
  );
}
